import { datadogLogs } from '@datadog/browser-logs'
import axios from 'axios'
import { AppState, AppStateContext } from 'containers/State/AppState'
import { PanelSupplierCode } from 'model/common'
import { coreRespondentUrl } from 'utils/coreRespondentUrl'
import { API_DISTRIBUTION_FACET } from 'utils/datadog'
import { useContext } from 'react'
import { shouldUseNewApiEndpoints } from 'utils/featureFlags'
import { useCintQueryParams } from './useCintQueryParams'
import { useMetadataQueryParams } from './useMetadataQueryParams'
import { usePostCallbackWithRetry } from './usePostCallback'
import { useQuestionnaireParams } from './useQuestionnaireParams'
import useSendRespondentInfo from './useSendRespondentInfo'
import { ApiRespondentStatus } from './useUpdateRespondent'

interface PanelSupplierRespondentData {
  member_id: string | null
  gender: string | null
  year_of_birth: number | null
  age: number | null
  panelist_id: string | null
  post_code: string | null
  panel_id: string | null
}

interface CreateRespondentPayload {
  order_number?: number | null
  panel_supplier_code: PanelSupplierCode
  panel_supplier_data: PanelSupplierRespondentData
  meta_data: Record<string, unknown>
  locale?: {
    language: string
    country: string
  } | null
  recaptcha_user_token?: string | null
}

const useCreateRespondent = () => {
  const mandatoryQueryParams = useQuestionnaireParams()
  const cintQueryParams = useCintQueryParams()
  const metadataQueryParams = useMetadataQueryParams()

  const {
    renderedQuestionnaire: [questionnaireData],
    selectedLanguage: [selectedLanguage]
  } = useContext<AppState>(AppStateContext)
  const post = usePostCallbackWithRetry<CreateRespondentPayload>()
  const sendRespondentInfoLegacy = useSendRespondentInfo()

  const createRespondentViaNewApi = async (
    recaptchaUserToken?: string
  ): Promise<ApiRespondentStatus | null> => {
    datadogLogs.logger.info('Routing create respondent to new endpoint', {
      apiUsed: API_DISTRIBUTION_FACET.NEW
    })

    const questionnaireLocale = questionnaireData?.questionnaire.locale
    const language = selectedLanguage ?? questionnaireLocale?.language
    const country = questionnaireLocale?.country
    const locale =
      language && country ? { language, country } : questionnaireLocale || null

    const payload: CreateRespondentPayload = {
      panel_supplier_code:
        mandatoryQueryParams.panelSupplier as PanelSupplierCode,
      panel_supplier_data: {
        member_id: cintQueryParams.memberId,
        gender: cintQueryParams.gender,
        year_of_birth: cintQueryParams.yearOfBirth,
        age: cintQueryParams.age,
        panelist_id: cintQueryParams.panelistId,
        post_code: cintQueryParams.postCode,
        panel_id: cintQueryParams.panelId
      },
      meta_data: metadataQueryParams,
      locale,
      recaptcha_user_token: recaptchaUserToken || null
    }

    const url = coreRespondentUrl(
      `/surveys/${mandatoryQueryParams.surveyId}/respondents/supplier/${mandatoryQueryParams.respondentId}`
    )

    try {
      const timeStart = performance.now()
      await post(payload, url)
      const timeEnd = performance.now()
      datadogLogs.logger.info('Respondent created via new API', {
        duration: timeEnd - timeStart
      })
      return null
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 409) {
        const existingStatus = err.response.data?.respondent_status as
          | ApiRespondentStatus
          | undefined
        if (!existingStatus) {
          datadogLogs.logger.error(
            'Create respondent returned 409 but respondent_status is missing',
            { responseData: err.response.data }
          )
          return null
        }

        datadogLogs.logger.info(
          'Create respondent returned 409: respondent already exists',
          {
            respondentStatus: existingStatus
          }
        )
        return existingStatus
      }

      datadogLogs.logger.error('Network Failure: Create respondent failed', {
        err,
        payload,
        payloadSize: JSON.stringify(payload).length
      })
      // Re-throw so the caller (InfoPage) can keep the user on /info
      // instead of navigating into the questionnaire without a respondent row.
      throw err
    }
  }

  const createRespondent: (
    recaptchaUserToken?: string
  ) => Promise<ApiRespondentStatus | null> = async (recaptchaUserToken) => {
    if (!mandatoryQueryParams) {
      // NOTE: should never get here
      datadogLogs.logger.error(
        'Create respondent failed. Mandatory params are missing. Ensure `sid`, `rid` and `ps` are specified. If they are, respondent progress is missing'
      )
      return null
    }

    const useNewApi = shouldUseNewApiEndpoints(
      mandatoryQueryParams.respondentId
    )

    if (useNewApi) {
      return createRespondentViaNewApi(recaptchaUserToken)
    }

    // Use legacy endpoint via useSendRespondentInfo
    await sendRespondentInfoLegacy(recaptchaUserToken)
    return null
  }

  return createRespondent
}

export default useCreateRespondent
